import React, { useState, useEffect } from 'react';
import AdsCard from '../AdsCard';
import instagram_ads_icon from '../../../../assests/instagram.png';
import { toast } from 'react-toastify';
import {
  getInstagramAdAccounts,
  storeInstagramIntegrationCredentials,
} from '../../../../services/ApiService';
import {
  clearInstagramOAuthState,
  clearOAuthCallbackParams,
  getOAuthRedirectUri,
} from '../../../../utils/oauthRedirect';
import { getIntegrationErrorMessage } from '../../../../utils/oauthErrors';
import { ConfirmModal } from '../../../ConfirmModal';
import { useNavigate } from 'react-router-dom';

const instagramOAuthDevLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Instagram OAuth Dev]', ...args);
  }
};

const InstagramAdsIntegration = ({
  isAccountLinked,
  fetchAdsAccountStatus,
  accountDetails,
  social_media_type,
}) => {
  const navigate = useNavigate();
  const [connecting, setConnecting] = useState(false);
  const [confirmInstagramAccountModal, setConfirmInstagramAccountModal] = useState(false);

  const resetInstagramConnect = () => {
    setConnecting(false);
    setConfirmInstagramAccountModal(false);
    clearInstagramOAuthState();
  };

  const openConfirmModal = () => {
    setConnecting(false);
    setConfirmInstagramAccountModal(true);
  };

  const handleConnect = async () => {
    setConnecting(true);
    setConfirmInstagramAccountModal(false);

    try {
      localStorage.setItem('selected_platform_to_connect', 'instagram');
      localStorage.removeItem('instagram_code');

      instagramOAuthDevLog('Connect confirmed', { redirect_uri: getOAuthRedirectUri() });

      await storeInstagramIntegrationCredentials();
    } catch (e) {
      console.error('[Instagram OAuth Dev] Connect failed', e);
      toast.error(getIntegrationErrorMessage(e, 'Failed to connect Instagram.'));
      resetInstagramConnect();
    }
  };

  const finishOAuthCallback = () => {
    clearInstagramOAuthState();
    clearOAuthCallbackParams(navigate);
  };

  const handleFetchAdAccounts = async (code) => {
    setConnecting(true);

    try {
      const result = await getInstagramAdAccounts(code);

      if (result?.success) {
        toast.success('Instagram account connected successfully');
        fetchAdsAccountStatus?.();
        finishOAuthCallback();
      } else {
        toast.error(getIntegrationErrorMessage(result, 'Failed to connect Instagram.'));
        finishOAuthCallback();
      }
    } catch (e) {
      console.error('[Instagram OAuth Dev] Callback failed', e);
      toast.error(getIntegrationErrorMessage(e, 'Failed to connect Instagram.'));
      finishOAuthCallback();
    } finally {
      setConnecting(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let code = params.get('code');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    const selectedPlatform = localStorage.getItem('selected_platform_to_connect');

    if (code) {
      code = code.split('#')[0] ?? '';
    }

    if (selectedPlatform !== 'instagram') {
      return;
    }

    if (error) {
      toast.error(
        errorDescription ||
          `Instagram authorization failed. Add this redirect URL in Meta Developer Console: ${getOAuthRedirectUri()}`,
      );
      finishOAuthCallback();
      setConnecting(false);
      return;
    }

    if (code) {
      handleFetchAdAccounts(code);
    }
  }, []);

  return (
    <>
      <AdsCard
        disabled={false}
        isAccountLinked={isAccountLinked}
        handleConnect={openConfirmModal}
        btn_title="Connect"
        connecting={connecting}
        platform={social_media_type === 'paid_social' ? 'Instagram Ads' : 'Instagram'}
        platform_icon={instagram_ads_icon}
        description="Instagram is a popular social media platform that allows users to share photos and videos, connect with others, and explore visual content."
        accountDetails={accountDetails}
        social_media_type={social_media_type}
        fetchAdsAccountStatus={fetchAdsAccountStatus}
      />

      <ConfirmModal
        show={confirmInstagramAccountModal}
        onClose={resetInstagramConnect}
        title={<h5 className="fs-4 fw-medium">Connect Your Instagram Account</h5>}
        body={
          <p className="fs-6">
            To fetch Instagram posts, connect your <b>Instagram Business or Creator account</b> through
            Facebook. The account must be linked to a <b>Facebook Page</b>.
          </p>
        }
        closeBtnTitle="Cancel"
        submitBtnTitle="Continue with Instagram"
        onSubmit={handleConnect}
        loadingOnsubmit={connecting}
      />
    </>
  );
};

export default InstagramAdsIntegration;
